import { commandUrl, rcuHost, statusUrl } from './addressing';
import type { RcuConfig } from '../model';
import { hasTarget } from '../model';
import type { RcuDocument } from '../wire';

/**
 * The direct HTTP channel to the RCU, as far as a browser can take it.
 *
 * The firmware has no CORS layer, so the app cannot read its replies: a JSON
 * `Content-Type` would also trigger a preflight the device answers with 405.
 * Every request is therefore sent `no-cors` as `text/plain`, which the firmware
 * parses all the same, and "delivered" means the request reached the device —
 * not that the command was applied. Confirming that needs the ack channel
 * (MQTT or the UDP broadcast), which a browser cannot join; a backend relay is
 * the clean place for it.
 */

/** The firmware's own socket timeout, so a hung request fails as fast as it does. */
export const RCU_REQUEST_TIMEOUT_MS = 5_000;

/** How the transport reports back. It never throws; callers show a toast. */
export interface CommandDelivery {
  readonly ok: boolean;
  /** URL the command was addressed to, shown in the console log. */
  readonly endpoint: string;
  readonly message: string;
}

const offlineDelivery = (): CommandDelivery => ({
  ok: false,
  endpoint: '',
  message: 'No RCU address configured.',
});

/** One request with a deadline, so a silent device cannot hang the guest UI. */
const request = async (url: string, init: RequestInit): Promise<boolean> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RCU_REQUEST_TIMEOUT_MS);
  try {
    await fetch(url, { ...init, mode: 'no-cors', signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Sends one already-built document. This is the console's fallback path and the
 * guest path's only one; the payload is not inspected, so callers own its shape.
 */
export const sendDocument = async (
  config: RcuConfig,
  document: RcuDocument,
): Promise<CommandDelivery> => {
  const identity = config.rcu;
  const endpoint = commandUrl(identity);

  if (!hasTarget(identity) || endpoint === null) return offlineDelivery();

  const reached = await request(endpoint, {
    method: 'POST',
    // Safelisted type: no preflight. The firmware ignores the header anyway.
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(document),
  });

  if (!reached) {
    return { ok: false, endpoint, message: `No answer from ${rcuHost(identity) ?? 'the RCU'}.` };
  }

  console.info(`[rcu] ${endpoint} :: ${JSON.stringify(document)}`);
  return { ok: true, endpoint, message: 'Delivered; the reply arrives over MQTT.' };
};

/**
 * Commissioning check: does something answer at this address? The reply itself
 * stays opaque, so this proves reachability only.
 */
export const probeRcu = async (
  config: RcuConfig,
): Promise<CommandDelivery & { readonly host: string | null }> => {
  const identity = config.rcu;
  const endpoint = statusUrl(identity);
  const host = rcuHost(identity);

  if (!hasTarget(identity) || endpoint === null) return { ...offlineDelivery(), host };

  const reached = await request(endpoint, { method: 'GET' });
  return reached
    ? { ok: true, endpoint, host, message: `Answered at ${host}.` }
    : {
        ok: false,
        endpoint,
        host,
        message: `Nothing answered at ${host}. Check the network and the serial number.`,
      };
};

/** True once commands have somewhere to go. */
export const canReachRcu = (config: RcuConfig): boolean =>
  hasTarget(config.rcu) && commandUrl(config.rcu) !== null;
